import React, { useEffect, useState } from "react";
import { X, Download, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import StlViewer from "./StlViewer"; // Importar o novo componente

const ImageModal = ({ image, isOpen, onClose, formatDate }) => {
  const [zoom, setZoom] = useState(1);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 4;
  const STEP = 0.25;

  // Detecta se é PDF
  const isPdf =
    (image?.mime_type || "").toLowerCase().includes("pdf") ||
    (image?.original_filename || "").toLowerCase().endsWith(".pdf");

  // Detecta se é STL
  const isStl =
    (image?.mime_type || "").toLowerCase().includes("model/stl") ||
    (image?.original_filename || "").toLowerCase().endsWith(".stl");

  // Verifica se é um tipo que suporta zoom (Imagem ou PDF)
  const supportsZoom = !isStl;

  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen, image?.id]);

  const downloadImage = () => {
    // Se tivermos base64, usa data: URL (funciona para imagem, PDF e STL)
    if (image?.image_data) {
      const mime = image.mime_type || "application/octet-stream";
      const link = document.createElement("a");
      // Para STL, é melhor forçar o download em vez de tentar abrir no browser
      const href = isStl 
        ? `data:application/octet-stream;base64,${image.image_data}`
        : `data:${mime};base64,${image.image_data}`;
        
      link.href = href;
      link.download = image.original_filename || `arquivo_${image.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Fallback: se não tiver base64, apenas avisa
    window.alert("Arquivo não está disponível para download no momento.");
  };

  const handleZoomIn = () => {
    if (supportsZoom) {
      setZoom((z) => Math.min(MAX_ZOOM, +(z + STEP).toFixed(2)));
    }
  };

  const handleZoomOut = () => {
    if (supportsZoom) {
      setZoom((z) => Math.max(MIN_ZOOM, +(z - STEP).toFixed(2)));
    }
  };

  const handleResetZoom = () => {
    if (supportsZoom) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (!supportsZoom || zoom <= 1) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheelZoom = (e) => {
    if (!supportsZoom) return;

    e.preventDefault();

    const direction = e.deltaY > 0 ? -1 : 1;

    setZoom((currentZoom) => {
      const nextZoom = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, +(currentZoom + direction * STEP).toFixed(2))
      );

      if (nextZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }

      return nextZoom;
    });
  };

  if (!isOpen || !image) return null;

  console.log("ImageModal: isStl", isStl);
  console.log("ImageModal: image.image_data length", image.image_data ? image.image_data.length : 0);
  console.log("ImageModal: image.mime_type", image.mime_type);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-white rounded-lg w-full ${isStl ? 'max-w-7xl h-[95vh]' : 'max-w-6xl max-h-[95vh]'} overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="flex justify-between items-center p-4 border-b">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {image.original_filename || "Arquivo"}
            </h3>
            <p className="text-sm text-gray-600 truncate">
              Tipo:{" "}
              {image.image_type
                ? image.image_type.replace("_", " ")
                : isPdf
                ? "PDF"
                : isStl
                ? "Escaneamento Intraoral (STL)" // Novo texto para STL
                : "Imagem"}
              {" • "}
              Data: {formatDate(image.created_at)}
            </p>
          </div>

          <div className="flex items-center space-x-2">
            {/* Controles de zoom (apenas para imagem e PDF) */}
            {supportsZoom && (
              <div className="flex items-center bg-gray-100 rounded-full px-2 py-1 mr-2">
                <button
                  type="button"
                  onClick={handleZoomOut}
                  className="p-1 text-gray-600 hover:text-gray-900"
                  title="Diminuir zoom"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="mx-1 text-xs text-gray-700 min-w-[3rem] text-center">
                  {(zoom * 100).toFixed(0)}%
                </span>
                <button
                  type="button"
                  onClick={handleZoomIn}
                  className="p-1 text-gray-600 hover:text-gray-900"
                  title="Aumentar zoom"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleResetZoom}
                  className="p-1 text-gray-500 hover:text-gray-900 ml-1"
                  title="Resetar zoom"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              onClick={downloadImage}
              className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Corpo: visualização */}
        <div className="p-4 flex-1 flex flex-col gap-4">
	          {/* Área principal de visualização */}
            <div
              className={`flex-1 w-full ${isStl ? 'h-full' : 'h-[75vh]'} bg-gray-50 border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center`}
              onWheel={handleWheelZoom}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
            {isStl && image.image_data ? (
	              // Visualizador STL
	              <StlViewer base64Data={image.image_data} key={image.id} />
            ) : isPdf ? (
              image.image_data ? (
                // PDF com zoom (escala no container do iframe)
                <div
                  className="inline-block"
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "top center",
                    transition: "transform 0.1s ease-out",
                  }}
                >
                  <iframe
                    title="Visualização de PDF"
                    src={`data:${
                      image.mime_type || "application/pdf"
                    };base64,${image.image_data}`}
                    className="w-[1000px] h-[80vh] border-0 bg-white"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 w-full px-4 text-center">
                  <p className="text-gray-600 mb-2">
                    PDF carregado, mas o conteúdo não está disponível para
                    visualização inline.
                  </p>
                  <p className="text-gray-500 text-sm mb-3">
                    Tente fazer o download para visualizar o arquivo.
                  </p>
                  <button
                    onClick={downloadImage}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Baixar PDF
                  </button>
                </div>
              )
            ) : image.image_data ? (
              // Imagem com zoom
              <div
                className="inline-flex items-center justify-center"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: isDragging ? "none" : "transform 0.1s ease-out",
                  cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                }}
                onMouseDown={handleMouseDown}
              >
                <img
                  src={`data:${image.mime_type};base64,${image.image_data}`}
                  alt="Visualização completa"
                  draggable={false}
                  className="block max-w-full max-h-[75vh] object-contain select-none"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 w-full">
                <p className="text-gray-500">
                  {isStl ? "Carregando modelo 3D..." : "Imagem não disponível"}
                </p>
              </div>
            )}
          </div>

          {/* Descrição */}
          {image.description && (
            <div className="mt-1">
              <h4 className="font-medium text-gray-900 mb-1">Descrição:</h4>
              <p className="text-gray-600 text-sm">{image.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
